<template>
    <div>
        <div class="campaign-edit campaign-edit-position">
            <EmptyCampaign v-if="!sandboxMode && !isInitialLoad && !hasTemplates" />

            <SandboxNoTemplates v-else-if="sandboxMode && !hasTemplates" @retry="onSandboxNoTemplatesRetry" />

            <EditorHeader
                v-if="hasTemplates"
                :has-meta-platform="hasMetaPlatform"
                @saveChanges="onSaveChanges"
                @publishToPlatform="refreshCampaignData"
                @toggleMetaPreview="toggleMetaPreview"
            />

            <div
                v-if="hasTemplates && !isInitialLoad"
                ref="body"
                class="campaign-edit__body campaign-edit__deliverables-wrapper dark-scrollbar"
            >
                <MissingFontInfo />

                <template v-if="sandboxMode || hasVisibleGroupValues">
                    <DeliverablePreview
                        v-for="masterTemplateId in masterTemplateIds"
                        :key="deliverableKey(masterTemplateId)"
                        class="campaign-edit__deliverables"
                        :master-template="masterTemplates[masterTemplateId]"
                        :max-height="bodyDimensionsPixels.height"
                        :max-width="bodyDimensionsPixels.width"
                        :scaling-factor="scalingFactor"
                        :is-meta-preview="isMetaPreview"
                        :playback-namespace="playbackNamespace"
                        :meta-overwrites="metaOverwrites"
                        @bannerLoaded="onBannerLoaded()"
                        @update="onUpdateTemplate"
                        @updateStarted="onUpdateStarted"
                        @familyUpdated="onUpdateTemplate"
                        @remove="onRemoveTemplate"
                        @newSize="showNewSizeModal"
                    />
                </template>

                <empty-message v-else>
                    <template #title>There's nothing to show here</template>
                    <template #content>
                        <p>If needed ask the campaign owner to share the content with you.</p>
                    </template>
                </empty-message>
            </div>

            <PreviewGroupControls
                v-if="hasTemplates"
                :scrubbar="{
                    currentTime: playbackController.currentTime.value,
                    duration: playbackController.duration.value
                }"
                replay-button
                @replay="playbackController.replay"
                @setBannerPlaybackTime="playbackController.seek"
            />

            <NewSizeModal
                v-if="newSizeModalIsVisible && newSizeSource"
                :master-template="newSizeSource"
                @close="closeNewSizeModal"
            />

            <FeedImportModal
                v-if="feedImportModalVisible"
                v-model:visible="feedImportModalVisible"
                :create-new-group-values="createNewGroupValuesAndSaveFeed"
                :group-name="feedImportGroupName"
                :updated-feed-url="updatedFeedUrl"
                :updated-primary-key="updatedPrimaryKey"
                @updateExternalFeedUrl="onFeedImportUpdate"
                @updatePrimaryKey="onUpdatePrimaryKey"
                @updateEditable="onImportUpdate"
                @close="hideFeedImportModal"
                @closeWithSuccess="closeFeedImportModalWithSuccess"
            />

            <Sidebar v-if="hasTemplates && hasVisibleGroupValues" :scroll="false" class="campaign-edit-sidebar">
                <div
                    class="sidebar-panes"
                    :class="{
                        'slide-to-left': !isSettingsPaneVisible || showSettingsPaneInModal,
                        'slide-to-right': isSettingsPaneVisible && !showSettingsPaneInModal
                    }"
                >
                    <div class="sidebar-panel sidebar-panel--groups">
                        <div class="sidebar-panel__inner-pane">
                            <EditableGroupList
                                class="groups-pane__element"
                                @add="onGroupAdd"
                                @duplicate="onGroupDuplicate"
                                @update="onGroupUpdate"
                                @remove="onGroupRemove"
                                @edit="onGroupEdit"
                                @importFeed="showFeedImportModal"
                                @refreshFeed="onRefreshExternalFeed"
                                @deleteFeed="onDeleteExternalFeed"
                            />
                        </div>
                    </div>
                    <div
                        v-show="isEditablesPaneVisible && !showEditablesPaneInModal"
                        class="sidebar-panel sidebar-panel--editables"
                    >
                        <EditablesListControls
                            v-if="hasSelectedEditables"
                            v-model:all-expanded="allEditablesExpanded"
                            v-model:search-value="searchValue"
                            @detach="onEditablesDetach"
                        />

                        <LabeledSwitch
                            v-if="showMetaPreviewToggle"
                            type="info"
                            :model-value="isMetaPreview"
                            class="preview-social__switch"
                            @change="toggleMetaPreview"
                        >
                            <div>Preview social</div>
                        </LabeledSwitch>

                        <EditablesList
                            class="attached-panel"
                            :all-expanded="allEditablesExpanded"
                            :search-value="searchValue"
                            @update="onEditableUpdate"
                            @remove="onOverwriteRemove"
                        />
                    </div>

                    <div
                        v-show="isSettingsPaneVisible && !showSettingsPaneInModal"
                        class="sidebar-panel sidebar-panel--settings"
                    >
                        <div class="sidebar-panel__header">
                            <Button
                                v-if="isSettingsPaneVisible && !showSettingsPaneInModal"
                                class="campaign-edit__header-action-btn"
                                size="small"
                                icon="md-swap"
                                @click="showSettingsPaneInModal = !showSettingsPaneInModal"
                            />
                        </div>

                        <div class="sidebar-panel__inner-pane">
                            <EditableSettingsPane @update="onEditableUpdate" />
                        </div>
                    </div>
                </div>
            </Sidebar>

            <alert-modal
                v-model="unsavedChangesModal"
                header="You have unsaved changes."
                title="Are you sure?"
                :content="unsavedChangesModalContent"
            >
                <template #footer>
                    <Button :disabled="isSavingChanges" @click="allowRouteLeaveAndDiscardChanges">Discard</Button>
                    <Button :loading="isSavingChanges" type="primary" @click="onSaveChanges">Save edits</Button>
                </template>
            </alert-modal>

            <hox-loading-layer
                v-if="loading"
                :is-opaque="isInitialLoad"
                :left-offset-pixels="60"
                size="large"
                theme="dark"
            />

            <DetachablePanel
                class-name="detached-editables"
                :closable="false"
                :width="singlePanelWidth"
                :model-value="isEditablesPaneVisible && showEditablesPaneInModal"
                @attach="showEditablesPaneInModal = false"
            >
                <template #title>Content</template>
                <template #content>
                    <EditablesList
                        class="dark-scrollbar dark-scrollbar--modal detached-panel"
                        :scrollable="false"
                        @update="onEditableUpdate"
                        @remove="onOverwriteRemove"
                    />
                </template>
            </DetachablePanel>

            <DetachablePanel
                class-name="detached-settings"
                :model-value="isSettingsPaneVisible && showSettingsPaneInModal"
                :attachable="!showEditablesPaneInModal"
                :width="singlePanelWidth"
                @attach="showSettingsPaneInModal = false"
                @close="isSettingsPaneVisible = false"
            >
                <template #content>
                    <EditableSettingsPane class="dark-scrollbar dark-scrollbar--modal" @update="onEditableUpdate" />
                </template>
            </DetachablePanel>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { type NavigationGuardNext, type RouteLocationNormalized } from "vue-router";
import { OverwriteScope } from "hoxton-shared/enums/overwrites";
import type { Editable, EditableValue, EditableWithOverwrites, MasterTemplate, Overwrite } from "hoxton-types";
import { OpenDcCampaignType } from "hoxton-shared/enums/opendc";

import bus from "@/bus";
import { deepClone } from "@/utils";
import * as editableUtils from "@/utils/editables";

import Editables from "@/services/Editables";
import MasterTemplates from "@/services/MasterTemplates";
import ImportExport from "@/services/ImportExport";

import { MediaLibraryAssetEvent } from "@/enums/mediaLibrary";
import { EditorAction, EditorGetters } from "@/store/modules/editor";

import { BannerNamespace, usePlaybackController } from "@/components/PlaybackControls/usePlaybackController";

import DeliverablePreview from "@/components/Campaign/DeliverablePreview.vue";
import Sidebar from "@/components/Sidebar.vue";
import EmptyCampaign from "@/components/Campaign/EmptyCampaign.vue";
import EditableGroupList from "@/components/Campaign/Editables/EditableGroupList.vue";
import EditablesList from "@/components/Campaign/Editables/EditablesList.vue";
import EditorHeader from "@/components/Campaign/EditorHeader/EditorHeader.vue";
import AlertModal from "@/components/Modal/Alert.vue";
import EmptyMessage from "@/components/common/EmptyMessage.vue";
import NewSizeModal from "@/components/Campaign/NewSizeModal.vue";
import SandboxNoTemplates from "@/components/Sandbox/NoTemplates.vue";
import EditablesListControls from "@/components/Campaign/Editables/EditablesListControls.vue";
import FeedImportModal from "@/components/Campaign/FeedImportModal.vue";
import LabeledSwitch from "@/components/Campaign/LabeledSwitch.vue";
import PreviewGroupControls from "@/components/PlaybackControls/PreviewGroupControls.vue";

import deleteGroupFeed from "@/apollo/mutations/DeleteGroupFeed.gql";
import updateCampaignFeeds from "@/apollo/mutations/UpdateCampaignFeeds.gql";
import addGroupValuesMutation from "@/apollo/mutations/v2/AddEditableGroupValues.gql";
import fontsQuery from "@/apollo/queries/FontsByClient.gql";
import campaignEditorQuery from "@/apollo/queries/v2/CampaignEditor.gql";

import EditableSettingsPane from "@/views/Campaign/EditableSettingsPane.vue";
import MissingFontInfo from "@/views/Campaign/MissingFontInfo.vue";
import DetachablePanel from "@/views/Campaign/DetachablePanel.vue";

import { CampaignAction, CampaignGetters, type Getters as CampaignGettersType } from "@/store/modules/campaign";
import { UiAction } from "@/store/modules/ui";

import { useOverwrite } from "@/composables/useOverwrite";
import { useScalingBannerWrapper } from "@/composables/useScalingBannerWrapper";
import { EditorEvents } from "@/enums/editor";
import { PlatformTypeOption } from "@/enums/platforms";

import type {
    Asset,
    EditableGroupValue,
    EditableOverwrite,
    Err,
    MetaEditablesNames,
    MetaOverwrites,
    UpdatedEditable,
    UpdatedOverwrite
} from "@/types/types";
import type { LanguageCode } from "@/utils/languages";
import { AppRouteNames } from "@/router/constants";
import generateUniqueGroupName from "@/utils/generateUniqueGroupName";

export default defineComponent({
    name: "CampaignEdit",
    components: {
        FeedImportModal,
        DetachablePanel,
        MissingFontInfo,
        NewSizeModal,
        EditableSettingsPane,
        EmptyMessage,
        PreviewGroupControls,
        EmptyCampaign,
        EditorHeader,
        DeliverablePreview,
        EditablesList,
        EditableGroupList,
        Sidebar,
        AlertModal,
        SandboxNoTemplates,
        EditablesListControls,
        LabeledSwitch
    },

    beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
        if (this.localOverwritesMade) {
            this.unsavedChangesModal = true;
            this.unsavedChangesCallback = (goToNext: boolean) => {
                if (goToNext) {
                    next();
                } else {
                    next(false);
                }
            };
        } else {
            next();
        }
    },

    props: {
        sandboxMode: {
            type: Boolean,
            default: false
        }
    },

    emits: [],

    setup() {
        const body = ref(null);
        const { bodyDimensionsPixels, scalingFactor, setBodyDimensionsPixels } = useScalingBannerWrapper({
            bodyRef: body
        });
        const { getOverwrite, removeOverwrite, shouldRemoveOverwrite } = useOverwrite();

        const playbackNamespace = BannerNamespace.Edit;
        const playbackController = usePlaybackController(playbackNamespace);

        return {
            body,
            bodyDimensionsPixels,
            scalingFactor,
            setBodyDimensionsPixels,
            getOverwrite,
            removeOverwrite,
            shouldRemoveOverwrite,

            playbackController,
            playbackNamespace
        };
    },

    data() {
        return {
            allEditablesExpanded: false,
            bannersUpdateCount: {} as Record<string, number>,
            bannerLoadCounter: 0,
            externalFeedUrl: "",
            isInitialLoad: true,
            isSavingChanges: false,
            loading: true,
            feedImportModalVisible: false,
            feedImportGroupName: "",
            searchValue: "",
            newSizeModalIsVisible: false,
            newSizeSource: null as MasterTemplate | null,
            primaryKey: "",
            refreshTriggeredAfterSave: false,
            singlePanelWidth: 250,
            unsavedChangesModal: false,
            unsavedChangesCallback: null as ((goToNext: boolean) => void) | null,
            updatedFeedUrl: "",
            updatedPrimaryKey: "",
            isFeedImportUpdating: false,
            isMetaPreview: false,
            metaOverwrites: {} as MetaOverwrites,
            showMetaPreviewToggle: false,
            importExportService: null as ImportExport | null,
            editablesService: null as Editables | null,
            masterTemplateService: null as MasterTemplates | null,
            unsubscribeLanguageWatcher: null as (() => void) | null,
            bannersInitialLoad: true
        };
    },

    computed: {
        unsavedChangesModalContent() {
            return "Your content edits have not been saved. If you leave this page, your edits will be lost.<br><br><b>Your existing score may no longer reflect the final content.</b>";
        },

        canListMasterTemplates() {
            return (
                this.canUseSandbox ||
                this.$auth.userCan(this.$auth.Actions.CanListMasterTemplates, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        canManageOverwrites() {
            return (
                this.canUseSandbox ||
                this.$auth.userCan(this.$auth.Actions.CanManageOverwrites, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        canUseSandbox() {
            return (
                this.sandboxMode &&
                this.$auth.userCan(this.$auth.Actions.CanUseSandbox, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            );
        },

        campaignId() {
            return this.$route.params.campaignId as string;
        },

        clientId() {
            if (this.sandboxMode) {
                return this.$store.state.campaign.client._id;
            }

            return this.$route.params.clientId as string;
        },

        editableOverwrites() {
            return this.$store.getters[EditorGetters.editableOverwrites] as EditableWithOverwrites[];
        },

        hasMetaPlatform() {
            return this.editableOverwrites.some(overwrite => overwrite.editable.publisherPlatform === "facebook");
        },

        graphqlContext() {
            return {
                useLocalDevLink: this.sandboxMode
            };
        },

        hasVisibleGroupValues() {
            const { editableGroupIds, editableGroups, editables } = this.$store.state.campaign.normalized;

            if (
                this.canUseSandbox ||
                this.$auth.userCan(this.$auth.Actions.CanManageRestrictedEditables, {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                })
            ) {
                return !!editableGroupIds.length;
            }

            const groupsWithVisibleEditables = editableGroupIds.filter(editableGroupId =>
                editableGroups[editableGroupId].editables.some(editableId => !editables[editableId].restricted)
            );

            return !!groupsWithVisibleEditables.length;
        },

        hasSelectedEditables() {
            return !!this.$store.getters[EditorGetters.selectedEditables].length;
        },

        hasTemplates() {
            return !!this.masterTemplateIds.length;
        },

        isEditablesPaneVisible: {
            get() {
                return this.$store.state.editor.isEditablesPaneVisible;
            },
            set(isVisible: boolean) {
                this.$store.dispatch(EditorAction.SetEditablesPaneVisibility, isVisible);
            }
        },

        isSettingsPaneVisible: {
            get() {
                return this.$store.state.editor.isSettingsPaneVisible;
            },
            set(isVisible: boolean) {
                this.$store.dispatch(EditorAction.SetSettingsPaneVisibility, isVisible);
            }
        },

        isWppLogin() {
            return this.$store.state.auth.isWppLogin;
        },

        libraryUrl() {
            return this.$router.resolve({
                name: AppRouteNames.CAMPAIGN_LIBRARY,
                params: {
                    clientId: this.clientId,
                    campaignId: this.campaignId
                },
                query: this.isWppLogin ? { type: "wpp" } : undefined
            }).href;
        },

        localEditableOverwrites() {
            return this.$store.state.editor.localEditableOverwrites;
        },

        localOverwriteTmpId: {
            get() {
                return this.$store.state.editor.localOverwriteTmpId;
            },
            set(tmpId: string) {
                this.$store.dispatch(EditorAction.SetLocalOverwriteTmpId, tmpId);
            }
        },

        localOverwritesMade() {
            return (
                this.$store.state.editor.localEditableOverwrites.length > 0 ||
                this.$store.state.editor.removedOverwriteIds.length > 0
            );
        },

        masterTemplateIds() {
            return this.$store.state.campaign.normalized.masterTemplateIds;
        },

        masterTemplates() {
            return this.$store.state.campaign.normalized.masterTemplates;
        },

        selectedLanguage() {
            return this.$store.state.editor.selectedLanguage;
        },

        selectedGroupValues() {
            return this.$store.state.editor.selectedGroupValues;
        },

        showEditablesPaneInModal: {
            get() {
                return this.$store.state.editor.showEditablesPaneInModal;
            },
            set(useModal: boolean) {
                this.$store.dispatch(EditorAction.SetShowEditablesPaneInModal, useModal);
            }
        },

        showSettingsPaneInModal: {
            get() {
                return this.$store.state.editor.showSettingsPaneInModal;
            },
            set(useModal: boolean) {
                this.$store.dispatch(EditorAction.SetShowSettingsPaneInModal, useModal);
            }
        }
    },

    watch: {
        canManageOverwrites: {
            immediate: true,
            handler() {
                if (!this.canManageOverwrites) {
                    this.$snackbar.warning("You do not have access to manage overwrites");
                    this.$router.push({ name: AppRouteNames.CAMPAIGN_LIBRARY }).catch(err => {
                        if (err.name !== "NavigationDuplicated") {
                            throw err;
                        }
                    });
                }
            }
        },

        hasTemplates: {
            immediate: true,
            handler(val) {
                this.$store.dispatch(
                    this.hasVisibleGroupValues &&
                        val &&
                        (this.canUseSandbox ||
                            this.$auth.userCan(this.$auth.Actions.CanListGroupValue, {
                                clientId: this.clientId,
                                campaignId: this.campaignId
                            }))
                        ? UiAction.OpenSidebar
                        : UiAction.CloseSidebar
                );
            }
        },

        hasSelectedEditables: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.isEditablesPaneVisible = true;
                } else {
                    this.isEditablesPaneVisible = false;
                }
            }
        },

        isEditablesPaneVisible: {
            immediate: true,
            handler(isVisible) {
                if (isVisible) {
                    const multiplier = this.showEditablesPaneInModal ? 1 : 2;
                    this.$store.dispatch(UiAction.SetSidebarWidthOpen, this.singlePanelWidth * multiplier);
                }
            }
        },

        showEditablesPaneInModal: {
            immediate: true,
            handler(showInModal) {
                if (showInModal) {
                    this.showSettingsPaneInModal = true;
                    this.$store.dispatch(UiAction.SetSidebarWidthOpen, this.singlePanelWidth);
                } else {
                    const multiplier = this.isEditablesPaneVisible ? 2 : 1;
                    this.$store.dispatch(UiAction.SetSidebarWidthOpen, this.singlePanelWidth * multiplier);
                }
            }
        },

        async loading(val) {
            if (!val) {
                await this.$nextTick();
                this.setBodyDimensionsPixels();
            }
        },

        selectedGroupValues: {
            immediate: true,
            handler(val) {
                const selectedMetaGroupId =
                    val[PlatformTypeOption.facebookPlatform]?.groupValueId ??
                    val[PlatformTypeOption.platformMetadata]?.groupValueId ??
                    null;

                this.updateMetaOverwrites(selectedMetaGroupId);
            },
            deep: true
        }
    },

    created() {
        window.addEventListener("resize", this.setBodyDimensionsPixels, true);
        this.loading = true;
        this.importExportService = new ImportExport(this.$apollo, this.$store);
        // @ts-expect-error Services will be refactored in a different task
        this.editablesService = new Editables(this.$apollo, this.campaignId, { context: this.graphqlContext });
        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);

        this.unsubscribeLanguageWatcher = this.$store.watch(
            state => state.campaign.languages,
            this.watchLanguage.bind(this)
        );
        bus.$on(MediaLibraryAssetEvent.AssetRemoved, this.watchMediaLibraryAssetRemoved.bind(this));
        bus.$on(EditorEvents.AddTemplatesToCampaign, this.addNewTemplateToCampaign.bind(this));

        // Make sure our data is up to date
        this.$apollo.getClient().reFetchObservableQueries();

        // set domain if in sandbox mode
        if (this.sandboxMode) {
            document.domain = document.domain.includes("hogarth.ai") ? "hogarth.ai" : "hoxton.co";
            this.$store.dispatch(EditorAction.SetIsSandboxMode, true);
        }
    },

    beforeUnmount() {
        this.unsubscribeLanguageWatcher?.();
        this.$store.dispatch(EditorAction.ClearSelectedGroupValues);
    },

    methods: {
        async addEditableGroupValues(editableGroupName: string, values: string[]) {
            return this.$apollo.mutate<{
                addEditableGroupValues: EditableGroupValue[];
            }>({
                mutation: addGroupValuesMutation,
                variables: {
                    editableGroupName,
                    values,
                    campaignId: this.campaignId
                },
                context: this.graphqlContext
            });
        },

        async saveExternalFeed(editableGroupName: string) {
            return this.$apollo.mutate({
                mutation: updateCampaignFeeds,
                variables: {
                    campaignId: this.campaignId,
                    group: editableGroupName,
                    feedUrl: this.externalFeedUrl,
                    primaryKey: this.primaryKey
                }
            });
        },

        allowRouteLeaveAndDiscardChanges() {
            this.unsavedChangesCallback?.(true);
            this.$store.dispatch(EditorAction.ClearLocalChanges);
        },

        async addNewTemplateToCampaign(ids: string[]) {
            if (!ids.length) {
                return;
            }

            this.loading = true;

            await Promise.all(
                ids.map(id =>
                    this.masterTemplateService?.associate(id, this.campaignId).catch(err => {
                        const message = `Error associating template ${id}.`;
                        if (err.message.includes("Invalid editable names.")) {
                            const explain = err.message
                                .replace(/GraphQL error:/i, "")
                                .replace("Invalid editable names.", "");
                            this.$snackbar.error(`${message} ${explain}`);
                        } else {
                            this.$snackbar.error(`${message}. Please try again.`);
                        }
                    })
                )
            );

            this.refreshCampaignData();
            this.refreshFontsData();
        },

        async createNewGroupValues(groupName: string, newGroupValues: string[] = []) {
            const data = await this.addEditableGroupValues(groupName, newGroupValues);
            const createdGroups = data.data?.addEditableGroupValues || [];

            createdGroups.forEach(({ _id, value }) => {
                this.$store.dispatch(CampaignAction.AddEditableGroupValue, {
                    groupName,
                    editableGroupValue: { value, _id }
                });
            });

            return createdGroups;
        },

        async createNewGroupValuesAndSaveFeed(groupName: string, newGroupValues: string[] = []) {
            const createdGroups = await this.createNewGroupValues(groupName, newGroupValues);

            return createdGroups;
        },

        deliverableKey(masterTemplateId: string) {
            return `${masterTemplateId}-${
                this.bannersUpdateCount[masterTemplateId] ? this.bannersUpdateCount[masterTemplateId] : 0
            }`;
        },

        editableUpdate(updateData: UpdatedEditable, overwrite: EditableOverwrite) {
            // TODO simplify this function
            const editableUpdate = deepClone(updateData) as UpdatedEditable & { value?: EditableValue | null };
            const affectingGroups = editableUpdate.groupsAffectingEditable;
            const editableGroupValueIds: string[] = [];

            // Set mediaItem to null if not found for consistency
            if (typeof editableUpdate.mediaItem === "undefined") {
                editableUpdate.mediaItem = null;
            }

            // Find groups being affected by the editable
            affectingGroups.forEach(groupName => {
                if (groupName in this.selectedGroupValues) {
                    editableGroupValueIds.push(this.selectedGroupValues[groupName].groupValueId);
                }
            });

            // Unknown scope so return an empty object
            if (!Object.values(OverwriteScope).includes(editableUpdate.scope)) {
                return {};
            }

            // Set base properties of overwrite
            const updatedEditable = editableUtils.setScopeValues(editableUpdate.scope, {
                language: this.selectedLanguage,
                scope: editableUpdate.scope,
                editableGroupValueIds,
                masterTemplateId: editableUpdate.masterTemplateId
            });

            // Set tmp overwriteId
            updatedEditable._id = `tmpId-${this.localOverwriteTmpId}`;
            this.localOverwriteTmpId += 1;

            // Set value properties
            if (overwrite.valueFromScope === editableUpdate.scope) {
                updatedEditable.value =
                    editableUpdate.value || editableUpdate.value === "" ? editableUpdate.value : overwrite.value;
                updatedEditable.mediaItem = editableUpdate.mediaItem || overwrite.mediaItem;
            } else {
                updatedEditable.value = editableUpdate.value;
                updatedEditable.mediaItem = editableUpdate.mediaItem;
            }

            // overwrite.value is missing on media settings changes (position, size, etc.). Added the check to set overwrite.value to mediaItem.id
            if (!updatedEditable.value && updatedEditable.mediaItem?.id) {
                updatedEditable.value = updatedEditable.mediaItem.id;
            }

            if (updateData.settings) {
                updatedEditable.settings = editableUpdate.settings;
            } else {
                updatedEditable.settings = overwrite.settings || undefined;
            }

            return updatedEditable;
        },

        hideFeedImportModal() {
            this.externalFeedUrl = "";
            this.updatedFeedUrl = "";
            this.updatedPrimaryKey = "";
            this.feedImportModalVisible = false;
            this.isFeedImportUpdating = false;
            this.feedImportGroupName = "";
            this.refreshCampaignData();
        },

        async closeFeedImportModalWithSuccess(editableGroupName: string) {
            await this.saveExternalFeed(editableGroupName);
            this.hideFeedImportModal();
            this.$snackbar.success("Great! Your feed and variables are all set.");
        },

        onBannerLoaded() {
            this.onBannerLoadedSandbox();
            this.setBodyDimensionsPixels();
        },

        async onBannerLoadedSandbox() {
            if (!this.sandboxMode) {
                return;
            }

            this.bannerLoadCounter += 1;
            // For the banner first load we do not want to trigger the data refetch
            // Only run this function on the last banner load of the set
            if (this.bannersInitialLoad || this.bannerLoadCounter <= this.masterTemplateIds.length) {
                return;
            }

            this.bannersInitialLoad = false;
            this.bannerLoadCounter = 0;

            await this.refreshCampaignData();
        },

        async onImportUpdate(editableUpdate: UpdatedEditable) {
            try {
                await this.importExportService?.applyEditableUpdate(editableUpdate);
            } catch (err) {
                this.$snackbar.error((err as Err).message, "", {
                    duration: 0,
                    closable: true
                });
            }
        },

        onFeedImportUpdate(url: string) {
            this.externalFeedUrl = url;
        },

        onUpdatePrimaryKey(primaryKey: string) {
            this.primaryKey = primaryKey;
        },

        onRefreshExternalFeed(groupName: string) {
            this.isFeedImportUpdating = true;
            this.feedImportGroupName = groupName;

            const group = this.$store.state.campaign.groupFeedMappings.find(mapping => mapping.group === groupName);

            this.updatedFeedUrl = group ? group.feedUrl : "";
            this.updatedPrimaryKey = group ? group.primaryKey : "";

            if (!this.updatedFeedUrl || !this.updatedPrimaryKey) {
                this.$snackbar.error("No feed URL found for this group.");
                this.isFeedImportUpdating = false;
            } else {
                this.feedImportModalVisible = true;
                this.$snackbar.info("Feed is being refreshed.", "", {
                    closable: true,
                    duration: 5
                });
            }
        },

        async onDeleteExternalFeed(groupName: string) {
            try {
                const response = await this.$apollo.mutate({
                    mutation: deleteGroupFeed,
                    variables: {
                        campaignId: this.campaignId,
                        group: groupName
                    }
                });
                this.$snackbar.success("Your linked feed has been successfully removed");
                await this.refreshCampaignData();
                return response;
            } catch (error) {
                this.$snackbar.error((error as Err).message);
                throw error;
            }
        },

        onEditablesDetach() {
            this.showEditablesPaneInModal = true;
        },

        onEditableUpdate(editableUpdate: UpdatedEditable) {
            const { editable, masterTemplateId, groupsAffectingEditable } = editableUpdate;

            const overwrite = this.getOverwrite(editable, groupsAffectingEditable, masterTemplateId);
            const newOverwrite = this.editableUpdate(editableUpdate, overwrite);

            const savedOverwrite = this.$store.state.campaign.editableOverwrites
                .find(e => e.editable._id === editable._id)
                ?.overwrites?.find(
                    o =>
                        o.masterTemplateId === masterTemplateId &&
                        o.language === this.selectedLanguage &&
                        o.scope === newOverwrite.scope &&
                        o.editableGroupValueIds.some(id => newOverwrite?.editableGroupValueIds?.includes(id))
                );

            /*
             * If the overwrite has no settings, value or media item (or the values are unchanged) it should be removed
             * Otherwise it can be added and the computation will work out which properties over overwrites to put together
             */
            if (this.shouldRemoveOverwrite(overwrite, newOverwrite, savedOverwrite)) {
                this.removeOverwrite(overwrite._id);
            } else {
                const updatedEditables = editableUtils.mergeEditables(this.localEditableOverwrites, [
                    {
                        editable,
                        overwrites: [newOverwrite]
                    }
                ]);

                this.$store.dispatch(EditorAction.SetLocalEditableOverwrites, updatedEditables);
            }

            const selectedMetaGroupId =
                this.selectedGroupValues[PlatformTypeOption.facebookPlatform]?.groupValueId ??
                this.selectedGroupValues[PlatformTypeOption.platformMetadata]?.groupValueId ??
                null;

            if (selectedMetaGroupId) {
                this.updateMetaOverwrites(selectedMetaGroupId);
            }
        },

        async onGroupAdd({ groupName, value }: { groupName: string; value: string }) {
            // todo update optimistic updates
            try {
                const newGroupValue = await this.editablesService?.addEditableGroupValue(groupName, value);
                const newGroupValueId = newGroupValue?.data.addEditableGroupValue._id;

                this.$store.dispatch(CampaignAction.AddEditableGroupValue, {
                    groupName,
                    editableGroupValue: {
                        value,
                        _id: newGroupValueId
                    }
                });

                return newGroupValueId;
            } catch {
                this.$snackbar.error("We could not add your group. Try again.");
                return null;
            }
        },

        async onGroupDuplicate({ groupName, groupValueId }: { groupName: string; groupValueId: string }) {
            const groupValueNamesByGroup = this.$store.getters[
                CampaignGetters.groupValueNamesByGroup
            ] as CampaignGettersType["groupValueNamesByGroup"];

            const { editableGroupValuesNames, editables } = groupValueNamesByGroup[groupName];

            const groupValue = this.$store.state.campaign.editableGroupValues[groupValueId];

            const newGroupName = generateUniqueGroupName(groupValue.value, editableGroupValuesNames);
            const newGroupValueId = await this.onGroupAdd({ groupName, value: newGroupName });

            if (!newGroupValueId) {
                this.$snackbar.error("Group value duplication failed.");
                return;
            }

            editables.forEach(editableId => {
                this.editableOverwrites.forEach(({ editable, overwrites }) => {
                    if (editable._id === editableId) {
                        const overwritesToDuplicate = overwrites.filter(({ editableGroupValueIds }) =>
                            editableGroupValueIds.includes(groupValueId)
                        );

                        if (overwritesToDuplicate.length) {
                            this.addLocalOverwrite(editable, overwritesToDuplicate, groupValueId, newGroupValueId);
                        }
                    }
                }, []);
            });
            this.$snackbar.success("Group value was duplicated successfully");
        },

        async addLocalOverwrite(
            editable: Editable,
            overwrites: Overwrite[],
            groupValueId: string,
            newGroupValueId: string
        ) {
            const updatedEditable = {
                editable,
                overwrites: overwrites.map(overwrite => {
                    const _id = `tmpId-${this.localOverwriteTmpId}`;
                    this.localOverwriteTmpId += 1;
                    const matchingIndex = overwrite.editableGroupValueIds.indexOf(groupValueId);
                    const editableGroupValueIds = [...overwrite.editableGroupValueIds];
                    editableGroupValueIds.splice(matchingIndex, 1, newGroupValueId);

                    // mediaItem overwrites needs to have it's id set as a value when we are saving overwrite
                    return {
                        ...overwrite,
                        _id,
                        editableGroupValueIds,
                        ...(overwrite.mediaItem && !overwrite.value && { value: overwrite.mediaItem.id })
                    };
                })
            };

            const updatedEditables = editableUtils.mergeEditables(this.$store.state.editor.localEditableOverwrites, [
                updatedEditable
            ]);

            await this.$store.dispatch(EditorAction.SetLocalEditableOverwrites, updatedEditables);
        },

        onGroupUpdate(data: { groupValueId: string; groupName: string }) {
            this.$store.dispatch(EditorAction.SelectGroupValue, data);
            const campaignType = this.$store.state.campaign.opendcPlatformSettings?.campaignType;
            if (
                [PlatformTypeOption.platformMetadata, PlatformTypeOption.facebookPlatform].includes(data.groupName) &&
                campaignType &&
                [OpenDcCampaignType.socialVideo, OpenDcCampaignType.socialStatic].includes(
                    campaignType as typeof OpenDcCampaignType.socialVideo | typeof OpenDcCampaignType.socialStatic
                )
            ) {
                this.showMetaPreviewToggle = true;
            } else {
                this.showMetaPreviewToggle = false;
            }
        },

        async onGroupEdit(data: {
            groupName: string;
            groupValue: string;
            editableGroupValues: { editableGroupValueId: string; value: string }[];
        }) {
            const editableGroupValues = this.$store.state.campaign.editableGroupValues;
            const previousEditableGroupValue = editableGroupValues[data.editableGroupValues[0].editableGroupValueId];

            const newData = {
                ...data,
                editableGroupValues: previousEditableGroupValue
            };

            this.$store.dispatch(CampaignAction.UpdateEditableGroupValue, {
                groupName: data.groupName,
                editableGroupValue: {
                    _id: data.editableGroupValues[0].editableGroupValueId,
                    value: data.editableGroupValues[0].value
                }
            });

            try {
                await this.editablesService?.updateEditableGroupValues(data.editableGroupValues);
            } catch {
                this.$snackbar.error("We could not update your variation. Try again.");
                this.$store.dispatch(CampaignAction.UpdateEditableGroupValue, {
                    groupName: newData.groupName,
                    editableGroupValue: newData.editableGroupValues
                });
            }
        },

        async onGroupRemove(data: { editableGroupValueIds: string[]; groupName: string; groupValue: string }) {
            const editableGroupValueIds = data.editableGroupValueIds[0];
            const previousEditableGroupValue = this.$store.state.campaign.editableGroupValues[editableGroupValueIds];

            const newData = {
                ...data,
                editableGroupValue: previousEditableGroupValue
            };

            data.editableGroupValueIds.forEach(editableGroupValueId => {
                this.$store.dispatch(CampaignAction.RemoveEditableGroupValue, {
                    editableGroupValueId,
                    groupName: data.groupName
                });
            });

            try {
                await this.editablesService?.removeEditableGroupValues(data.editableGroupValueIds);
                data.editableGroupValueIds.forEach(editableGroupValueId => {
                    this.$store.dispatch(EditorAction.ClearGroupValueData, editableGroupValueId);
                });
            } catch (e) {
                this.$snackbar.error("We could not remove your group value", (e as Err).message);
                this.$store.dispatch(CampaignAction.AddEditableGroupValue, {
                    groupName: newData.groupName,
                    editableGroupValue: {
                        value: newData.editableGroupValue.value,
                        _id: newData.editableGroupValue._id
                    }
                });
            }
        },

        async onSandboxNoTemplatesRetry() {
            await this.refreshCampaignData();
        },

        onOverwriteRemove(editableOverwriteToRemove: UpdatedOverwrite) {
            const updatedEditables = this.localEditableOverwrites.map(editableOverwrite => {
                if (editableOverwrite.editable._id !== editableOverwriteToRemove.editable._id) {
                    return editableOverwrite;
                }

                const toUpdate = { ...editableOverwrite };
                const templateScopeFilter = (overwrite: Overwrite) =>
                    !(
                        overwrite.masterTemplateId === editableOverwriteToRemove.masterTemplateId &&
                        overwrite.language === editableOverwriteToRemove.language &&
                        overwrite.scope === editableOverwriteToRemove.scope
                    );

                const groupScopeFilter = (overwrite: Overwrite) =>
                    !(
                        overwrite.language === editableOverwriteToRemove.language &&
                        overwrite.scope === editableOverwriteToRemove.scope
                    );

                const filterFn =
                    editableOverwriteToRemove.scope === "MASTER_TEMPLATE" ||
                    editableOverwriteToRemove.scope === "EDITABLE_GROUP_AND_MASTER_TEMPLATE"
                        ? templateScopeFilter
                        : groupScopeFilter;

                toUpdate.overwrites = toUpdate.overwrites.filter(filterFn);

                return toUpdate.overwrites.length ? toUpdate : null;
            });

            this.$store.dispatch(
                EditorAction.SetLocalEditableOverwrites,
                updatedEditables.filter(up => !!up)
            );
        },

        showNewSizeModal(masterTemplate: MasterTemplate) {
            this.newSizeSource = masterTemplate;
            this.newSizeModalIsVisible = true;
        },

        async onRemoveTemplate(masterTemplateId: string) {
            try {
                await this.masterTemplateService?.disassociate(masterTemplateId, this.campaignId);
                this.$store.dispatch(CampaignAction.DisassociateMasterTemplate, masterTemplateId);
                this.loading = true;
                this.refreshCampaignData();
                this.refreshFontsData();
            } catch {
                this.$snackbar.error("Master Template could not be removed");
                this.loading = false;
            }
        },

        async onSaveChanges() {
            if (this.isSavingChanges) {
                return;
            }

            this.isSavingChanges = true;

            try {
                this.$snackbar.info("Saving local changes...");

                if (this.$store.state.editor.removedOverwriteIds.length) {
                    await this.editablesService?.removeOverwrites(this.$store.state.editor.removedOverwriteIds);
                }

                if (this.$store.state.editor.localEditableOverwrites.length) {
                    await this.editablesService?.addOverwrites(this.$store.state.editor.localEditableOverwrites);
                }

                this.refreshTriggeredAfterSave = true;
                await this.refreshCampaignData();

                this.$snackbar.success(
                    "Changes saved",
                    !this.sandboxMode
                        ? `All changes have been saved. Head to your <a href="${this.libraryUrl}">Creative Library</a> to preview your ads.`
                        : undefined
                );

                // If save was called from the unsaved modal then fire callback
                if (this.unsavedChangesCallback) {
                    this.unsavedChangesCallback(true);
                    this.unsavedChangesCallback = null;
                }
            } catch (e) {
                this.$snackbar.graphQLError(e as Error);
            }

            this.isSavingChanges = false;
        },

        async onUpdateStarted() {
            this.loading = true;
        },

        async onUpdateTemplate(masterTemplateIds: string[]) {
            this.loading = true;
            await this.refreshCampaignData();
            this.refreshFontsData();
            const templatesToReload = Array.isArray(masterTemplateIds) ? masterTemplateIds : [masterTemplateIds];
            templatesToReload.forEach(masterTemplateId => {
                const currentCount =
                    masterTemplateId in this.bannersUpdateCount ? this.bannersUpdateCount[masterTemplateId] : 0;

                this.bannersUpdateCount[masterTemplateId] = currentCount + 1;
            });
        },

        async refreshCampaignData() {
            if (this.$apollo.queries.editableGroupsByCampaign) {
                await this.$apollo.queries.editableGroupsByCampaign.refetch();
            }
        },

        async refreshFontsData() {
            if (this.$apollo.queries.fonts) {
                await this.$apollo.queries.fonts.refetch();
            }
        },

        showFeedImportModal(groupName: string) {
            this.feedImportGroupName = groupName;
            this.feedImportModalVisible = true;
        },

        closeNewSizeModal() {
            this.newSizeModalIsVisible = false;
            this.newSizeSource = null;
        },

        watchLanguage(languages: LanguageCode[], oldLanguages: LanguageCode[]) {
            oldLanguages.forEach(language => {
                if (!languages.includes(language)) {
                    this.$store.dispatch(EditorAction.ClearLanguageData, language);
                }
            });
        },

        watchMediaLibraryAssetRemoved(asset: Asset) {
            // remove local overwrites
            // todo: add support for recursive delete of files when parent dir is removed
            this.$store.dispatch(EditorAction.ClearLocalAssetChanges, asset);

            // make sure that the stored overwrites are updated.
            this.refreshCampaignData();
        },

        toggleMetaPreview() {
            this.isMetaPreview = !this.isMetaPreview;
        },

        updateMetaOverwrites(selectedMetaGroupId: string) {
            const results = {} as MetaOverwrites;

            this.editableOverwrites.forEach(item => {
                if (item.overwrites && item.overwrites.length > 0 && selectedMetaGroupId) {
                    item.overwrites.forEach(overwrite => {
                        if (
                            overwrite.editableGroupValueIds &&
                            overwrite.editableGroupValueIds.includes(selectedMetaGroupId)
                        ) {
                            if (typeof overwrite.value === "string") {
                                results[item.editable.name as MetaEditablesNames] = overwrite.value;
                            } else if (Array.isArray(overwrite.value)) {
                                const selectedValue = overwrite.value.find(value => value.selected === true);
                                if (selectedValue) {
                                    results[item.editable.name as MetaEditablesNames] = selectedValue.label;
                                }
                            }
                        }
                    });
                }
            });

            this.metaOverwrites = results;
        }
    },

    apollo: {
        editableGroupsByCampaign: {
            query: campaignEditorQuery,
            fetchPolicy: "network-only",
            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            skip() {
                return !this.canManageOverwrites;
            },

            /*
            Setting the context here allows us to tell the link chain whether or not queries should be redirected
            Not documented well in the Vue Apollo docs but they mention it here:
            https://apollo.vuejs.org/api/smart-query.html#options - You can also use any other watchQuery options
            Unfortunately the link they reference is broken. The resource it should point to is here:
            https://www.apollographql.com/docs/react/api/core/ApolloClient/#ApolloClient.watchQuery
            */
            context() {
                return this.graphqlContext;
            },

            async result({ data }) {
                if (
                    (!data?.campaign || data.campaign.client._id !== this.$route.params.clientId) &&
                    !this.sandboxMode
                ) {
                    this.$snackbar.warning("The campaign you're looking for doesn't exist or may have been deleted");

                    this.$router.push({
                        name: AppRouteNames.CLIENT_CAMPAIGNS,
                        params: { clientId: data?.campaign?.client?._id || this.$route.params.clientId }
                    });

                    this.loading = false;
                    this.isInitialLoad = false;
                    return;
                }

                const imageDimensionsByDefaultValue =
                    await editableUtils.getImageDimensionsByDefaultValueFromEditablesWithOverwrites(
                        data.editablesWithOverwritesByCampaign
                    );
                if (this.$store.state.campaign._id !== data.campaign._id) {
                    this.$store.dispatch(EditorAction.ResetEditor);
                    this.$store.dispatch(CampaignAction.SetInitialState);
                }
                this.$store.dispatch(CampaignAction.SetCampaign, data.campaign);
                const editablesWithOverwritesByCampaignWithDefaultImageDimensions =
                    editableUtils.getEditablesWithOverwritesByCampaignWithDefaultImageDimensions(
                        data.editablesWithOverwritesByCampaign,
                        imageDimensionsByDefaultValue
                    );
                this.$store.dispatch(
                    CampaignAction.SetEditableOverwrites,
                    editablesWithOverwritesByCampaignWithDefaultImageDimensions
                );
                this.$store.dispatch(CampaignAction.SetMasterTemplates, data.masterTemplatesByCampaign);
                const editableGroupsByCampaignWithDefaultImageDimensions =
                    editableUtils.getEditableGroupsByCampaignWithDefaultImageDimensions(
                        data.editableGroupsByCampaign,
                        imageDimensionsByDefaultValue
                    );
                this.$store.dispatch(CampaignAction.SetGroups, editableGroupsByCampaignWithDefaultImageDimensions);
                if (this.refreshTriggeredAfterSave) {
                    /*
                        Because the query result callback (this function) is now
                        async, we need to wait until it is finished because triggering
                        to clear local changes.

                        Previously we were clearing local changes directly
                        after the query refresh call, which was before the result
                        callback had time to finish, resulting in getting old
                        values (images/text) displayed in the banners.
                    */
                    this.refreshTriggeredAfterSave = false;
                    this.$store.dispatch(EditorAction.ClearLocalChanges);
                }

                if (!this.sandboxMode && !this.hasVisibleGroupValues && !this.canListMasterTemplates) {
                    this.$snackbar.warning(
                        "There's no content for you to edit. If you were expecting to see something here then please contact the campaign owner.",
                        "",
                        {
                            closable: true,
                            duration: 10
                        }
                    );
                    this.$router.push({ name: AppRouteNames.CAMPAIGN_LIBRARY }).catch(err => {
                        if (err.name !== "NavigationDuplicated") {
                            throw err;
                        }
                    });
                }

                this.loading = false;
                this.isInitialLoad = false;
            }
        },
        fonts: {
            query: fontsQuery,
            fetchPolicy: "no-cache",
            variables() {
                return {
                    clientId: this.clientId
                };
            },

            skip() {
                return !this.canManageOverwrites || this.sandboxMode;
            },

            async result({ data }) {
                this.$store.dispatch(EditorAction.SetFonts, data.fonts);
            }
        }
    }
});
</script>

<style lang="scss">
@use "sass:color";
@use "@style/variables" as *;

.campaign-edit-position {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
}

.campaign-edit {
    display: flex;
    flex-direction: column;
    .feed-import-modal--update {
        opacity: 0.01;
    }
}

.campaign-edit__deliverables {
    flex: 0 0 auto;
}

.campaign-edit__deliverables-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.campaign-edit__body {
    flex: 1;
    overflow: auto;
    position: relative;
    padding: 22px 20px;
}

.campaign-edit__header {
    padding: 0 20px;
    overflow-x: auto;
}

.add-templates-modal {
    .ivu-modal {
        top: 3vh;
    }

    .ivu-modal-body {
        display: flex;

        padding: 0;
        height: 85vh;

        .templates-modal {
            &__sidebar {
                display: flex;
                flex: 0 0 215px;
                width: 215px;
                flex-direction: column;
                justify-content: flex-start;
                align-items: center;
                background-color: $white;
                overflow-y: auto;

                &-filter {
                    width: 100%;
                    flex: 0 0;
                }

                .ivu-collapse {
                    background: $white;
                }

                .ivu-collapse > .ivu-collapse-item {
                    .ivu-collapse-header {
                        border: none;
                        background: $white;
                    }

                    &.ivu-collapse-item-active {
                        border-bottom: 1px solid $cmp-light-filters-border-color;

                        .ivu-collapse-header {
                            background: $cmp-light-filters-border-color;
                        }
                    }
                }
            }

            &__library {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            &__body {
                padding-top: 30px;
                min-width: 0;
                flex: 1;
            }

            &__body-content {
                height: 100%;
            }
        }
    }
}

.sidebar-backdrop {
    background-color: rgba(0, 0, 0, 0.5);
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    cursor: pointer;
}

.sidebar-panes {
    display: flex;
    justify-content: flex-start;
    position: fixed;
    left: 60px;
    top: 0;
    height: 100vh;
    width: 100%;
}

.sidebar-panel {
    flex: 0 0 250px;
    width: 250px;
    border-right: $cmp-dark-border-width solid $cmp-dark-border-color;

    overflow-y: auto;

    &__header {
        height: $campaign-nav-height;
        display: flex;
        justify-content: flex-end;
        align-items: flex-end;
        padding: 8px;
    }

    &__inner-pane {
        border-top: $cmp-dark-border-width solid $cmp-dark-border-color;
        margin-top: $campaign-nav-height;
    }

    &--settings {
        .sidebar-panel__inner-pane {
            margin-top: 0;
        }
    }

    .preview-social__switch {
        padding: 10px;
        display: flex;
        flex-direction: row-reverse;
        gap: 10px;
        align-items: baseline;
        align-items: center;
        justify-content: flex-end;
    }
}

.slide-to-left {
    transform: translateX(0px);
    transition: all 150ms linear 0s;
}

.slide-to-right {
    transform: translateX(-250px);
    transition: all 150ms linear 0s;
}

.campaign-edit-sidebar {
    overflow: hidden;
    position: relative;
}

.detached-editables .ivu-modal-content {
    top: 40px;
    left: calc(100% - 320px);
}

.detached-settings .ivu-modal-content {
    top: 80px;
    left: calc(100% - 310px);
}
</style>
